import { z } from 'zod'
import { ajouterJours } from './calendrier'
import { TYPES_ACCES_BANQUE, type TypeAccesBanque } from './domaine'

// Accès bancaires par un agrégateur DSP2 (Enable Banking) : lecture seule des opérations,
// sur consentement donné à la banque par un administrateur. Aucun identifiant bancaire
// n'est saisi ni gardé ici : la validation forte se fait chez la banque. Fonctions pures ;
// les services appliquent.

/** Plafond DSP2 du consentement, quelle que soit la banque. */
export const JOURS_CONSENTEMENT_MAX = 180
/** La tâche « renouveler » s'ouvre ce nombre de jours avant la fin du consentement. */
export const JOURS_AVANT_RENOUVELLEMENT = 7
/** Chaque relève relit ces jours avant la couverture (opérations comptabilisées en retard). */
export const JOURS_RELECTURE = 7
/** Premier passage sur un compte jamais relevé. */
export const JOURS_PREMIERE_RELEVE = 90
/**
 * Relève manuelle : au plus une par heure et par accès. La banque limite la lecture sans
 * l'utilisateur à quatre par jour ; le job en fait deux.
 */
export const MINUTES_ENTRE_RELEVES_MANUELLES = 60

const JOUR_MS = 86_400_000

/**
 * Fin du consentement à demander : le plafond de la banque (en secondes), borné à 180
 * jours, moins une minute de marge pour les horloges décalées.
 */
export function finConsentement(maintenant: Date, plafondSecondes: number | null): Date {
  const max = JOURS_CONSENTEMENT_MAX * JOUR_MS
  const plafond = plafondSecondes === null ? max : Math.min(plafondSecondes * 1000, max)
  return new Date(maintenant.getTime() + plafond - 60_000)
}

/**
 * Premier jour à demander à la banque pour un compte. Premier passage : le lendemain de la
 * couverture des imports CSV (les libellés diffèrent, on ne relit pas leurs jours), sinon
 * quatre-vingt-dix jours. Ensuite : sept jours avant la couverture, jamais avant le premier
 * jour relevé par l'agrégateur. Les lignes déjà connues sont dédoublonnées à l'import.
 */
export function debutReleve(e: {
  couvertJusquAu: string | null
  /** Premier jour relevé par l'agrégateur ; null : premier passage. */
  depuis: string | null
  aujourdhui: string
}): string {
  if (e.depuis === null) {
    if (e.couvertJusquAu === null) return ajouterJours(e.aujourdhui, -JOURS_PREMIERE_RELEVE)
    const lendemain = ajouterJours(e.couvertJusquAu, 1)
    return lendemain > e.aujourdhui ? e.aujourdhui : lendemain
  }
  if (e.couvertJusquAu === null) return e.depuis
  const relecture = ajouterJours(e.couvertJusquAu, -JOURS_RELECTURE)
  return relecture < e.depuis ? e.depuis : relecture
}

export type SuiviConsentement = 'valide' | 'a_renouveler' | 'expire'

export function suiviConsentement(fin: Date, maintenant: Date): SuiviConsentement {
  const reste = fin.getTime() - maintenant.getTime()
  if (reste <= 0) return 'expire'
  return reste <= JOURS_AVANT_RENOUVELLEMENT * JOUR_MS ? 'a_renouveler' : 'valide'
}

export function titreRenouvellement(banque: string, type: TypeAccesBanque): string {
  return `Renouveler l’accès ${banque} (${type})`
}

/**
 * Espace à demander : celui choisi s'il est proposé par la banque, sinon le premier qu'elle
 * propose (Swan n'a que l'espace professionnel). Liste vide : la banque ne dit rien, le
 * choix reste libre.
 */
export function typeAccesRetenu(
  proposes: readonly TypeAccesBanque[],
  choisi: TypeAccesBanque,
): TypeAccesBanque {
  return proposes.length === 0 || proposes.includes(choisi) ? choisi : (proposes[0] ?? choisi)
}

/** « FR76 •••• 0189 » : assez pour reconnaître un compte, rien de plus. */
export function masquerIban(iban: string | null | undefined): string | null {
  const brut = iban?.replace(/\s+/g, '').toUpperCase() ?? ''
  if (brut.length < 8) return null
  return `${brut.slice(0, 4)} •••• ${brut.slice(-4)}`
}

/** Commencer une connexion : la banque (nom exact chez l'agrégateur), son pays, l'espace. */
export const banqueConnexion = z.object({
  banque: z.string().trim().min(1).max(200),
  pays: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .default('FR'),
  type: z.enum(TYPES_ACCES_BANQUE),
})
export type BanqueConnexion = z.infer<typeof banqueConnexion>

/** Rattacher un compte remonté par la banque : à un compte de la fiche, ou à un nouveau. */
export const liaisonCompteBanque = z.union([
  z.object({ compteId: z.uuid() }).strict(),
  z.object({ entiteId: z.uuid(), libelle: z.string().trim().min(1).max(200) }).strict(),
])
export type LiaisonCompteBanque = z.infer<typeof liaisonCompteBanque>
